import type { AppointmentDTO } from 'src/types/dto/appointment.dto';
import type { AuthorizationDTO } from 'src/types/dto/authorization.dto';
import type { ActivityDTO, InspectorDTO } from 'src/types/dto';
import type { GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';

import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { gridClasses } from '@mui/x-data-grid';
import { AppDataGrid } from 'src/components/app-data-grid';

import { Box, Grid, Stack, Tooltip, Typography } from '@mui/material';

import { usePaginatedApi } from 'src/hooks/use-paginated-api';

import { endpoints } from 'src/utils/axios';
import { getReconciledAppointmentDisplayDurationDays } from 'src/utils/appointments';
import { formatDate } from 'src/utils/format-time';
import { formatAppointmentInstant } from 'src/utils/timezone';
import { getActivityDurationDays } from 'src/sections/visit/components/allocation-duration';

import { Iconify } from 'src/components/iconify';
import { UserIcon } from 'src/components/user-icon';
import { TagsList } from 'src/components/tags-list';
import { IconLabel } from 'src/components/IconLabel';
import { EmptyContent } from 'src/components/empty-content';

import { getAuthorizationName } from 'src/utils/competency';

/** MIT DataGrid maximum `pageSize`; backend loads up to `PAGE_OPTIONS_MAX_TAKE` for single-activity lists, reconciles, then returns this page. */
const GRID_MAX_PAGE_SIZE = 100;

type AppointmentTableData = {
  id: number;
  resource: InspectorDTO;
  role: string;
  authorizations: AuthorizationDTO[] | undefined;
  from: string | Date;
  to: string | Date;
  timezoneId?: string;
  durationDisplayDays: number;
  distance: number;
  travelFrom: number;
  travelTo: number;
  hotelNights: number;
  nearby: boolean;
  isBlocker: boolean;
};

export function AppointmentsDetails({
  visit,
  solutionId,
}: {
  visit: ActivityDTO;
  solutionId?: number | string;
}) {
  const { t, i18n } = useTranslation(['activities', 'organization']);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  // Inside a solution the tab reviews the proposed allocation, so it renders the
  // solution appointments carried on the activity instead of the live appointments.
  const isSolutionMode = solutionId != null;

  const solutionAppointments = useMemo<AppointmentDTO[]>(() => {
    if (!isSolutionMode) return [];
    return (visit.allocatedInspectors ?? []).flatMap((inspector) =>
      (inspector.solutionAppointments ?? []).map((appointment) => ({
        ...appointment,
        inspector: appointment.inspector ?? inspector,
      }))
    );
  }, [isSolutionMode, visit.allocatedInspectors]);

  const {
    data: fetchedAppointments,
    isLoading: fetchedAppointmentsLoading,
    pagination: appointmentsPagination,
  } = usePaginatedApi<AppointmentDTO>({
    endpoint: endpoints.appointments.root,
    paginationParams: {
      page: paginationModel.page + 1,
      take: paginationModel.pageSize,
    },
    additionalParams: {
      'activity-ids': visit.id.toString(),
    },
    enabled: !isSolutionMode,
  });

  const appointments = isSolutionMode ? solutionAppointments : fetchedAppointments;
  const appointmentsLoading = isSolutionMode ? false : fetchedAppointmentsLoading;
  const itemCount = isSolutionMode
    ? appointments.length
    : appointmentsPagination?.itemCount ?? 0;
  const [appInfo, setAppInfo] = useState<{
    distance: number;
    emissions: number;
    externalResources: number;
    hotelNights: number;
    nearby: number;
  }>({ distance: 0, emissions: 0, externalResources: 0, hotelNights: 0, nearby: 0 });
  const [tableData, setTableData] = useState<AppointmentTableData[]>([]);

  useEffect(() => {
    const list = appointments ?? [];
    const fromVisit = getActivityDurationDays(visit);
    const fromNested = list
      .map((appointment) =>
        appointment.activity ? getActivityDurationDays(appointment.activity as ActivityDTO) : 0,
      )
      .find((days) => days > 0);
    const activityDays = fromVisit > 0 ? fromVisit : fromNested ?? 0;
    const displayDaysById = getReconciledAppointmentDisplayDurationDays(list, activityDays);

    const data: AppointmentTableData[] = list.map((appointment) => {
      const rawDays = displayDaysById.get(Number(appointment.id)) ?? 0;
      return {
      id: Number(appointment.id),
      resource: appointment.inspector,
      role: appointment.inspector.role?.role ?? '',
      authorizations: appointment.inspector.authorizations,
      from: appointment.from,
      to: appointment.to,
      timezoneId: appointment.timezoneId,
      durationDisplayDays: rawDays,
      distance:
        appointment.travelDistance ??
        appointment.travelToSiteDistance ??
        appointment.travelFromSiteDistance ??
        0,
      travelFrom: appointment.travelFromSiteDistance ?? 0,
      travelTo: appointment.travelToSiteDistance ?? 0,
      hotelNights: +appointment.hotelNights,
      nearby: Boolean(appointment.nextNearbyAppointment || appointment.prevNearbyAppointment),
      isBlocker: appointment.type === 'blocker',
    };
    });
    data.sort((a, b) => new Date(a.from).getTime() - new Date(b.from).getTime());
    setTableData(data);
    setAppInfo({
      distance: data.reduce(
        (acc, appointment) => acc + appointment.travelTo + appointment.travelFrom,
        0
      ),
      emissions:
        data.reduce((acc, appointment) => acc + appointment.travelTo + appointment.travelFrom, 0) *
        (+(process.env.NEXT_PUBLIC_EMISSION_CONSTANT ?? 150)),
      externalResources: data.filter((appointment) => appointment.resource.type === 'EXTERNAL')
        .length,
      hotelNights: data.reduce((acc, appointment) => acc + appointment.hotelNights, 0),
      nearby: data.filter((appointment) => appointment.nearby).length,
    });
  }, [appointments, visit]);

  const columns: GridColDef<AppointmentTableData>[] = [
    {
      field: 'resource',
      headerName: t('activityModal.appointments.resource'),
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <UserIcon name={params.row.resource.name} type={params.row.resource.type} />
      ),
      type: 'string',
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'role',
      headerName: t('activityModal.appointments.role'),
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) =>
        params.row.isBlocker ? null : params.row.role || '-',
      type: 'string',
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'authorizations',
      headerName: t('activityModal.appointments.competencies'),
      flex: 3,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.isBlocker) return null;
        return (
          <Box id={`competencies-container-${params.row.id}`} sx={{ maxHeight: 24, width: '90%' }}>
            <TagsList
              containerIdentifier={`competencies-container-${params.row.id}`}
              tags={
                params.row.authorizations?.map((e: AuthorizationDTO) => ({
                  id: e.id,
                  label: getAuthorizationName(e, i18n),
                  color: visit.activityCompetencies?.some(
                    (ac) => ac.competency.id === e.competency.id
                  )
                    ? 'info'
                    : 'default',
                })) ?? []
              }
              color="info"
              showTooltip
            />
          </Box>
        );
      },
      type: 'string',
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'date',
      headerName: t('activityModal.appointments.date', { defaultValue: 'Date' }),
      flex: 1,
      minWidth: 110,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => formatDate(params.row.from) || '-',
      type: 'string',
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'durationDisplayDays',
      headerName: t('activityModal.appointments.duration'),
      flex: 1,
      minWidth: 80,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const n = Number(params.row.durationDisplayDays);
        const label = n > 0 ? n.toString() : '';
        const showDurationTooltip = !params.row.isBlocker && label !== '';
        const tooltipTitle = (
          <Stack spacing={0.75} sx={{ py: 0.25, maxWidth: 360 }}>
            <Typography variant="caption" component="div" sx={{ lineHeight: 1.35 }}>
              {t('activityModal.details.sections.team.from')}
            </Typography>
            <Typography variant="caption" component="div" sx={{ lineHeight: 1.4 }}>
              {formatAppointmentInstant(params.row.from, params.row.timezoneId)}
            </Typography>
            <Typography variant="caption" component="div" sx={{ lineHeight: 1.35 }}>
              {t('activityModal.details.sections.team.to')}
            </Typography>
            <Typography variant="caption" component="div" sx={{ lineHeight: 1.4 }}>
              {formatAppointmentInstant(params.row.to, params.row.timezoneId)}
            </Typography>
          </Stack>
        );
        return (
          <Tooltip title={tooltipTitle} placement="top" arrow enterDelay={400}>
            <Box
              component="span"
              tabIndex={showDurationTooltip ? 0 : -1}
              sx={{
                cursor: showDurationTooltip ? 'help' : 'default',
                borderBottom: showDurationTooltip ? '1px dotted' : 'none',
                borderColor: 'divider',
                '&:focus-visible': { outline: (theme) => `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
              }}
            >
              {label}
            </Box>
          </Tooltip>
        );
      },
      type: 'string',
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'distance',
      headerName: t('activityModal.appointments.distance'),
      flex: 1,
      minWidth: 80,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) =>
        params.row.isBlocker ? null : params.row.distance || '-',
      type: 'string',
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'travelTo',
      headerName: t('activityModal.appointments.travelTo'),
      flex: 1,
      minWidth: 80,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.isBlocker) {
          return params.row.travelTo ? `${params.row.travelTo}km` : '-';
        }
        return params.row.travelTo ? `${params.row.travelTo}km` : '-';
      },
      type: 'string',
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'travelFrom',
      headerName: t('activityModal.appointments.travelFrom'),
      flex: 1,
      minWidth: 80,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.isBlocker) {
          return params.row.travelFrom ? `${params.row.travelFrom}km` : '-';
        }
        return params.row.travelFrom ? `${params.row.travelFrom}km` : '-';
      },
      type: 'string',
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'hotelNights',
      headerName: t('activityModal.appointments.hotelNights'),
      flex: 1,
      minWidth: 80,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) =>
        params.row.isBlocker ? null : params.row.hotelNights || '-',
      type: 'string',
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'nearby',
      headerName: t('activityModal.appointments.nearby'),
      flex: 1,
      minWidth: 80,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) =>
        params.row.isBlocker
          ? null
          : params.row.nearby
            ? <Iconify icon="ic:round-check-circle" color="success.main" />
            : null,
      type: 'string',
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'blocker',
      headerName: '',
      flex: 0.5,
      minWidth: 50,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) =>
        params.row.isBlocker ? <Iconify icon="mdi:briefcase" /> : null,
      type: 'string',
      align: 'left',
      headerAlign: 'left',
    },
  ];
  return (
    <Stack px={2}>
      <Typography variant="h4" py={2}>
        {visit.name}
      </Typography>
      <Box p={2}>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid xs={12} md={3}>
            <IconLabel
              label={`${itemCount} ${t('activityModal.appointments.appointments', { count: itemCount })}`}
              icon="ic:baseline-location-on"
              fontSize={16}
            />
          </Grid>
          <Grid xs={12} md={3}>
            <IconLabel
              label={`${Math.floor(appInfo.distance)}km ${t('activityModal.appointments.distance')}`}
              icon="ic:outline-route"
              fontSize={16}
            />
          </Grid>
          <Grid xs={12} md={3}>
            <IconLabel
              label={`${Math.floor(appInfo.emissions)}kg of CO2`}
              icon="ic:baseline-airplanemode-active"
              fontSize={16}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid xs={12} md={3}>
            <IconLabel
              label={`${appInfo.externalResources} ${t('activityModal.appointments.externalResources', { count: appInfo.externalResources })}`}
              icon="solar:users-group-rounded-bold-duotone"
              fontSize={16}
            />
          </Grid>
          <Grid xs={12} md={3}>
            <IconLabel
              label={`${appInfo.hotelNights} ${t('activityModal.appointments.hotelNights', { count: appInfo.hotelNights })}`}
              icon="mdi:bed-outline"
              fontSize={16}
            />
          </Grid>
          <Grid xs={12} md={3}>
            <IconLabel
              label={`${appInfo.nearby} ${t('activityModal.appointments.nearby', { count: appInfo.nearby })}`}
              icon="ic:outline-alt-route"
              fontSize={16}
            />
          </Grid>
        </Grid>
      </Box>
      <AppDataGrid
        rows={tableData}
        loading={appointmentsLoading}
        rowHeight={56}
        rowCount={itemCount}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        paginationMode={isSolutionMode ? 'client' : 'server'}
        pageSizeOptions={[25, 50, GRID_MAX_PAGE_SIZE]}
        columns={columns}
        getRowId={(row) => row.id}
        getRowClassName={(params) => (params.row.isBlocker ? 'blockerRow' : '')}
        slots={{
          noRowsOverlay: () => <EmptyContent title={t('list.empty')} />,
          noResultsOverlay: () => <EmptyContent title={t('list.noResults')} />,
        }}
        columnHeaderHeight={32}
        hideFooterPagination={itemCount <= paginationModel.pageSize}
        hideFooter={itemCount <= paginationModel.pageSize}
        sx={{
          [`& .${gridClasses.cell}`]: {
            alignItems: 'center',
            display: 'inline-flex',
            padding: '8px',
          },
          '&.MuiDataGrid-root': { height: 'auto' },
          '.MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-row.blockerRow': {
            bgcolor: 'action.hover',
            '&:hover': {
              bgcolor: 'action.selected',
            },
          },
        }}
      />
    </Stack>
  );
}
